<template>
  <div class="teaching-log">
    <!-- 搜索栏 -->
    <div class="search-bar">
      <van-search v-model="studentName" placeholder="搜索学生姓名" shape="round" background="transparent"
        @search="onSearch" />
      <div class="filter-btn" @click="showFilter = true">
        <van-icon name="filter-o" size="18" color="#666" />
      </div>
    </div>

    <!-- 课程列表 -->
    <div class="log-list" ref="listRef" @scroll="handleScroll">
      <div v-for="(dayLogs, date) in groupedTeachingLogs" :key="date" class="date-section">
        <div class="date-header">
          <span class="date-text">{{ date }}</span>
          <span class="week-text">{{ dayLogs[0].week }}</span>
          <span class="count-badge">{{ dayLogs.length }}节课</span>
        </div>
        <div class="card-list">
          <div v-for="(item, idx) in dayLogs" :key="idx" class="course-card"
            :class="{ completed: item.signStatus === 2 }" @click="goToDetail(item.schedulePlanId)">
            <div class="card-left">
              <div class="time-badge">
                <span class="time">{{ item.startTime }}</span>
                <span class="time-divider">-</span>
                <span class="time">{{ item.endTime }}</span>
              </div>
            </div>
            <div class="card-content">
              <div class="course-name">{{ item.courseName }}</div>
              <div class="student-row">
                <van-icon name="friends-o" size="12" />
                <span class="student-names">{{ item.studentNames }}</span>
              </div>
              <div class="teacher-tag">
                <van-icon name="user-o" size="12" />
                <span>{{ item.teacherName }}</span>
              </div>
            </div>
            <div class="card-right">
              <van-icon name="arrow" color="#ccc" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="loading" class="list-loading">
        <van-loading type="spinner" size="16px" color="#1677ff" />
        <span>加载中...</span>
      </div>
      <div v-if="!hasMore && !loading && Object.keys(groupedTeachingLogs).length > 0" class="list-end">
        没有更多了
      </div>
      <van-empty v-if="!hasMore && !loading && Object.keys(groupedTeachingLogs).length === 0" description="暂无考勤记录" />
    </div>

    <!-- 筛选弹窗 -->
    <van-popup v-model:show="showFilter" position="bottom" round closeable
      :style="{ bottom: 'calc(56px + env(safe-area-inset-bottom))' }" safe-area-inset-bottom>
      <div class="filter-panel">
        <div class="filter-title">筛选条件</div>
        <div class="filter-section">
          <div class="filter-label">教师筛选</div>
          <van-field v-model="teacherSearch" placeholder="搜索教师姓名" left-icon="search"
            @keyup.enter="filterTeachers(teacherSearch)" />
          <div v-if="teacherSearchResults.length > 0" class="teacher-list">
            <div v-for="teacher in teacherSearchResults" :key="teacher.userId" class="teacher-item"
              :class="{ selected: isTeacherSelected(teacher) }" @click="toggleTeacher(teacher)">
              {{ teacher.nickName }}
              <van-icon v-if="isTeacherSelected(teacher)" name="success" />
            </div>
          </div>
          <div v-if="selectedTeachers.length > 0" class="selected-tags">
            <van-tag v-for="teacher in selectedTeachers" :key="teacher.userId" closable type="primary"
              @close="removeTeacher(teacher.userId)">
              {{ teacher.nickName }}
            </van-tag>
          </div>
        </div>
        <div class="filter-footer">
          <van-button type="default" @click="resetFilter">重置</van-button>
          <van-button type="primary" @click="confirmFilter">确定</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onActivated, defineOptions } from 'vue'
import { useRouter } from 'vue-router'
import { getSchedulePlanListForPage } from '@/api/schedule'
import { getTeacherList } from '@/api/user'
import { debounce } from '@/utils/common'

defineOptions({ name: 'TeachingLog' })

const router = useRouter()
const showFilter = ref(false)
const teacherSearch = ref('')
const teacherSearchResults = ref([])
const allTeachers = ref([])
const selectedTeachers = ref([])

const pageNum = ref(1)
const pageSize = ref(10)
const groupedTeachingLogs = ref({})
const studentName = ref('')
const loading = ref(false)
const hasMore = ref(true)


onMounted(() => {
  loadAllTeachers()
  getSchedulePlanList()
})

onActivated(() => {
  if (pageNum.value > 1) {
    pageNum.value = 1
    groupedTeachingLogs.value = {}
    hasMore.value = true
    getSchedulePlanList()
  }
})

function loadAllTeachers() {
  getTeacherList().then(res => {
    allTeachers.value = res
    teacherSearchResults.value = res
  })
}

const filterTeachers = debounce((keyword) => {
  if (keyword.trim()) {
    teacherSearchResults.value = allTeachers.value.filter(t =>
      t.nickName && t.nickName.includes(keyword)
    )
  } else {
    teacherSearchResults.value = allTeachers.value
  }
}, 300)

function isTeacherSelected(teacher) {
  return selectedTeachers.value.some(t => t.userId === teacher.userId)
}

function toggleTeacher(teacher) {
  const index = selectedTeachers.value.findIndex(t => t.userId === teacher.userId)
  if (index === -1) {
    selectedTeachers.value.push(teacher)
  } else {
    selectedTeachers.value.splice(index, 1)
  }
}

function removeTeacher(teacherId) {
  const index = selectedTeachers.value.findIndex(t => t.userId === teacherId)
  if (index !== -1) {
    selectedTeachers.value.splice(index, 1)
  }
}

function resetFilter() {
  teacherSearch.value = ''
  teacherSearchResults.value = allTeachers.value
  selectedTeachers.value = []
}

function confirmFilter() {
  showFilter.value = false
  onSearch()
}

function onSearch() {
  pageNum.value = 1
  groupedTeachingLogs.value = {}
  hasMore.value = true
  getSchedulePlanList()
}

function getSchedulePlanList() {
  if (loading.value || !hasMore.value) return
  loading.value = true
  const params = {
    pageNum: pageNum.value,
    pageSize: pageSize.value,
    studentName: studentName.value || undefined,
    teacherIds: selectedTeachers.value.map(t => t.userId)
  }
  getSchedulePlanListForPage(params).then(response => {
    if (response) {
      groupedTeachingLogs.value = {
        ...groupedTeachingLogs.value,
        ...response
      }
    }
    if (!response || Object.keys(response).length < 5) {
      hasMore.value = false
    }
    pageNum.value++
  }).finally(() => {
    loading.value = false
  })
}

function handleScroll(event) {
  const scrollTop = event.target.scrollTop
  const scrollHeight = event.target.scrollHeight
  const clientHeight = event.target.clientHeight
  if (scrollTop + clientHeight >= scrollHeight - 20) {
    getSchedulePlanList()
  }
}

function goToDetail(schedulePlanId) {
  router.push({
    path: '/teachingLogDetail',
    query: { schedulePlanId }
  })
}
</script>

<style scoped>
.teaching-log {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

/* 搜索栏 */
.search-bar {
  background-color: #fff;
  padding: 8px 12px 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.search-bar .van-search {
  flex: 1;
  padding: 0;
}

.search-bar :deep(.van-search__content) {
  background-color: #f5f7fa;
}

.filter-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  border-radius: 18px;
}

/* 统计卡片 */
.stats-card {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
  margin: 12px 16px;
  border-radius: 12px;
  padding: 16px 0;
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.2);
  flex-shrink: 0;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-value.success {
  color: #b7eb8f;
}

.stat-value.warning {
  color: #ffd591;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.stat-divider {
  width: 1px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.2);
}

/* 列表区域 */
.log-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 16px 16px;
}

.log-list::-webkit-scrollbar {
  display: none;
}

/* 日期分组 */
.date-section {
  margin-bottom: 16px;
}

.date-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-top: 4px;
}

.date-text {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.week-text {
  font-size: 12px;
  color: #999;
}

.count-badge {
  margin-left: auto;
  font-size: 11px;
  color: #1677ff;
  background-color: #e6f4ff;
  padding: 2px 8px;
  border-radius: 10px;
}

/* 课程卡片 */
.card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.course-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.course-card.completed {
  opacity: 0.85;
}

.course-card:active {
  transform: scale(0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.card-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 10px;
  background: linear-gradient(180deg, #f0f7ff 0%, #e6f0ff 100%);
  min-width: 80px;
  gap: 6px;
}

.time-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.time {
  font-size: 13px;
  font-weight: 600;
  color: #1677ff;
}

.time-divider {
  font-size: 10px;
  color: #91caff;
}

.card-content {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.course-name {
  font-size: 15px;
  font-weight: 500;
  color: #262626;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #8c8c8c;
}

.student-names {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.teacher-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #1677ff;
  background-color: #e6f4ff;
  padding: 2px 8px;
  border-radius: 8px;
  width: fit-content;
}

.card-right {
  display: flex;
  align-items: center;
  padding-right: 12px;
}

/* 加载和结束提示 */
.list-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  font-size: 13px;
  color: #999;
}

.list-end {
  text-align: center;
  padding: 20px;
  font-size: 12px;
  color: #ccc;
}

/* 筛选弹窗 */
.filter-panel {
  padding: 20px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.filter-title {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 20px;
}

.filter-section {
  flex: 1;
}

.filter-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
}

.teacher-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}

.teacher-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.teacher-item:last-child {
  border-bottom: none;
}

.teacher-item.selected {
  background-color: #e6f4ff;
  color: #1677ff;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.filter-footer {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.filter-footer .van-button {
  flex: 1;
}
</style>
